import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ConnectionStatus = 'idle' | 'searching' | 'connected' | 'disconnected';

export interface Message {
  id: string;
  content: string;
  senderId: string;   // socket.id — 'me' if from current user
  isMe: boolean;
  timestamp: number;
  type: 'TEXT' | 'SYSTEM' | 'ATTACHMENT';
  flagged?: boolean;
  attachment?: {
    dataUrl: string;
    mimeType: string;
    filename: string;
  };
}

export type GenderType = 'MALE' | 'FEMALE' | 'OTHER';
export type FriendRequestStatus = 'idle' | 'sent' | 'received' | 'accepted' | 'rejected';

const FREE_MATCH_LIMIT = 10;

interface ChatState {
  status: ConnectionStatus;
  messages: Message[];
  roomId: string | null;
  strangerTyping: boolean;
  isTyping: boolean;
  genderFilter: 'FEMALE' | null;
  reportedUserId: string | null;
  currentStrangerId: string | null;
  soundEnabled: boolean;

  // Guest identity
  guestDisplayName: string | null;
  myGender: GenderType | null;
  needsGuestSetup: boolean;   // true when non-member hasn't set name/gender yet

  // Stranger info
  strangerGender: GenderType | null;
  strangerDisplayName: string | null;
  strangerIsMember: boolean;
  strangerId: string | null;

  // Friend request state (during an active chat)
  friendRequestStatus: FriendRequestStatus;
  friendRequestFrom: { id: string; name: string } | null;

  // Free match tracking (female-filter matches used by non-premium users)
  femaleFilterMatchesUsed: number;
  showPaywall: boolean;

  // Guest setup trigger — used by sidebar to request the modal in ChatWindow
  guestSetupPendingGender: 'FEMALE' | 'ANY' | null;

  setStatus: (status: ConnectionStatus) => void;
  addMessage: (msg: Message) => void;
  addSystemMessage: (content: string) => void;
  setRoomId: (id: string | null) => void;
  setStrangerTyping: (v: boolean) => void;
  setIsTyping: (v: boolean) => void;
  setGenderFilter: (f: 'FEMALE' | null) => void;
  setReportedUserId: (id: string | null) => void;
  setCurrentStrangerId: (id: string | null) => void;
  setSoundEnabled: (v: boolean) => void;
  clearChat: () => void;
  resetSession: () => void;

  setGuestDisplayName: (name: string) => void;
  setMyGender: (gender: GenderType) => void;
  setNeedsGuestSetup: (v: boolean) => void;
  setStrangerGender: (gender: GenderType | null) => void;
  setStrangerDisplayName: (name: string | null) => void;
  setStrangerIsMember: (v: boolean) => void;
  setStrangerId: (id: string | null) => void;
  setFriendRequestStatus: (s: FriendRequestStatus) => void;
  setFriendRequestFrom: (f: { id: string; name: string } | null) => void;
  incrementFemaleFilterMatches: () => void;
  setShowPaywall: (v: boolean) => void;
  freeMatchesLeft: () => number;
  setGuestSetupPendingGender: (v: 'FEMALE' | 'ANY' | null) => void;
  lifetimeConnections: number;
  incrementLifetimeConnections: () => void;
  setLifetimeConnections: (n: number) => void;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      status: 'idle',
      messages: [],
      roomId: null,
      strangerTyping: false,
      isTyping: false,
      genderFilter: null,
      reportedUserId: null,
      currentStrangerId: null,
      soundEnabled: true,

      guestDisplayName: null,
      myGender: null,
      needsGuestSetup: true,

      strangerGender: null,
      strangerDisplayName: null,
      strangerIsMember: false,
      strangerId: null,

      friendRequestStatus: 'idle' as FriendRequestStatus,
      friendRequestFrom: null,

      femaleFilterMatchesUsed: 0,
      showPaywall: false,

      guestSetupPendingGender: null,

      lifetimeConnections: 0,

      setStatus: (status) => set({ status }),

      addMessage: (msg) =>
        set((state) => ({ messages: [...state.messages, msg] })),

      addSystemMessage: (content) =>
        set((state) => ({
          messages: [
            ...state.messages,
            {
              id: `sys-${Date.now()}`,
              content,
              senderId: 'system',
              isMe: false,
              timestamp: Date.now(),
              type: 'SYSTEM',
            },
          ],
        })),

      setRoomId: (roomId) => set({ roomId }),
      setStrangerTyping: (strangerTyping) => set({ strangerTyping }),
      setIsTyping: (isTyping) => set({ isTyping }),
      setGenderFilter: (genderFilter) => set({ genderFilter }),
      setReportedUserId: (reportedUserId) => set({ reportedUserId }),
      setCurrentStrangerId: (currentStrangerId) => set({ currentStrangerId }),
      setSoundEnabled: (soundEnabled) => set({ soundEnabled }),

      setGuestDisplayName: (name) => set({ guestDisplayName: name, needsGuestSetup: false }),
      setMyGender: (gender) => set({ myGender: gender }),
      setNeedsGuestSetup: (v) => set({ needsGuestSetup: v }),
      setStrangerGender: (gender) => set({ strangerGender: gender }),
      setStrangerDisplayName: (name) => set({ strangerDisplayName: name }),
      setStrangerIsMember: (v) => set({ strangerIsMember: v }),
      setStrangerId: (id) => set({ strangerId: id }),
      setFriendRequestStatus: (s) => set({ friendRequestStatus: s }),
      setFriendRequestFrom: (f) => set({ friendRequestFrom: f }),

      incrementFemaleFilterMatches: () =>
        set((state) => ({
          femaleFilterMatchesUsed: state.femaleFilterMatchesUsed + 1,
        })),

      setShowPaywall: (showPaywall) => set({ showPaywall }),

      setGuestSetupPendingGender: (guestSetupPendingGender) => set({ guestSetupPendingGender }),

      incrementLifetimeConnections: () =>
        set((state) => ({ lifetimeConnections: state.lifetimeConnections + 1 })),

      setLifetimeConnections: (n) => set({ lifetimeConnections: n }),

      freeMatchesLeft: () => Math.max(0, FREE_MATCH_LIMIT - get().femaleFilterMatchesUsed),

      clearChat: () =>
        set({
          messages: [],
          strangerTyping: false,
          isTyping: false,
          currentStrangerId: null,
          strangerGender: null,
          strangerDisplayName: null,
          strangerIsMember: false,
          strangerId: null,
          friendRequestStatus: 'idle',
          friendRequestFrom: null,
        }),

      resetSession: () =>
        set({
          status: 'idle',
          messages: [],
          roomId: null,
          strangerTyping: false,
          isTyping: false,
          currentStrangerId: null,
          strangerGender: null,
          strangerDisplayName: null,
          strangerIsMember: false,
          strangerId: null,
          friendRequestStatus: 'idle',
          friendRequestFrom: null,
        }),
    }),
    {
      name: 'sw-chat',
      partialize: (state) => ({
        soundEnabled: state.soundEnabled,
        femaleFilterMatchesUsed: state.femaleFilterMatchesUsed,
        lifetimeConnections: state.lifetimeConnections,
      }),
      // Clear any stale guest identity fields that may have been persisted
      // by older versions of the store before the partialize config was updated.
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.guestDisplayName = null;
          state.myGender = null;
          state.needsGuestSetup = true;
        }
      },
    }
  )
);

export { FREE_MATCH_LIMIT };
